/**
 * Web Speech API: speech-to-text (recognition) and text-to-speech (synthesis).
 * Works in many mobile Safari / Chrome / Capacitor WebViews when the OS allows it.
 */

const getRecognitionCtor = () => {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

export const isSpeechRecognitionSupported = () => Boolean(getRecognitionCtor());

export const isSpeechSynthesisSupported = () =>
  typeof window !== 'undefined' && Boolean(window.speechSynthesis);

/** Strip coach command tags before speaking. */
const stripCommandTags = (s) =>
  String(s || '')
    .replace(/\[[A-Z_]+:\s*[^\]]+\]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * @param {{ lang?: string, onInterim?: (t: string) => void, onFinal?: (t: string) => void, onError?: (e: Error) => void }} opts
 * @returns {{ stop: () => void }}
 */
export const startSpeechToText = ({
  lang = 'en-US',
  onInterim,
  onFinal,
  onError,
} = {}) => {
  const Ctor = getRecognitionCtor();
  if (!Ctor) {
    onError?.(new Error('Speech recognition is not supported on this device or browser.'));
    return { stop: () => {} };
  }

  const recognition = new Ctor();
  recognition.lang = lang;
  recognition.interimResults = true;
  recognition.continuous = false;
  recognition.maxAlternatives = 1;

  recognition.onresult = (event) => {
    let interim = '';
    let finalChunk = '';
    for (let i = event.resultIndex; i < event.results.length; i += 1) {
      const res = event.results[i];
      const piece = String(res[0]?.transcript || '');
      if (res.isFinal) finalChunk += piece;
      else interim += piece;
    }
    const trimmedInterim = interim.trim();
    if (trimmedInterim) onInterim?.(trimmedInterim);
    const trimmedFinal = finalChunk.trim();
    if (trimmedFinal) onFinal?.(trimmedFinal);
  };

  recognition.onerror = (event) => {
    const code = String(event?.error || '');
    if (code === 'aborted' || code === 'no-speech') return;
    onError?.(new Error(code || 'Speech recognition failed'));
  };

  try {
    recognition.start();
  } catch (e) {
    onError?.(e instanceof Error ? e : new Error(String(e)));
    return { stop: () => {} };
  }

  return {
    stop: () => {
      try {
        recognition.stop();
      } catch {
        /* ignore */
      }
    },
  };
};

export const stopSpeaking = () => {
  try {
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
  } catch {
    /* ignore */
  }
};

/**
 * @param {string} text
 * @param {{ ttsLang?: string, ttsRate?: number, ttsVoiceURI?: string }} prefs
 */
export const speakAloud = (text, prefs = {}) =>
  new Promise((resolve, reject) => {
    if (!isSpeechSynthesisSupported()) {
      reject(new Error('Text-to-speech is not supported on this device or browser.'));
      return;
    }
    const plain = stripCommandTags(text);
    if (!plain) {
      resolve();
      return;
    }

    const utter = new SpeechSynthesisUtterance(plain);
    utter.lang = prefs.ttsLang || 'en-US';
    utter.rate = Math.min(1.5, Math.max(0.5, Number(prefs.ttsRate) || 1));
    const uri = String(prefs.ttsVoiceURI || '').trim();
    if (uri) {
      const voices = window.speechSynthesis.getVoices();
      const match = voices.find((v) => v.voiceURI === uri);
      if (match) utter.voice = match;
    }
    utter.onend = () => resolve();
    utter.onerror = () => reject(new Error('Text-to-speech was interrupted or failed.'));
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utter);
  });

export const loadSpeechVoices = () =>
  new Promise((resolve) => {
    if (!isSpeechSynthesisSupported()) {
      resolve([]);
      return;
    }
    const read = () => window.speechSynthesis.getVoices() || [];
    const finish = () => resolve(read());
    const initial = read();
    if (initial.length) {
      finish();
      return;
    }
    const onVoices = () => {
      window.speechSynthesis.onvoiceschanged = null;
      finish();
    };
    window.speechSynthesis.onvoiceschanged = onVoices;
    window.setTimeout(() => {
      if (window.speechSynthesis.onvoiceschanged === onVoices) {
        window.speechSynthesis.onvoiceschanged = null;
        finish();
      }
    }, 800);
  });
